import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by status
const createIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
};

const statusColors = {
  criada: '#3B82F6',        // blue
  aguardando_inicio: '#F59E0B', // amber
  em_andamento: '#8B5CF6',  // purple
  finalizada: '#10B981',    // green
  cancelada: '#EF4444',     // red
  aguardando_validacao: '#6366F1', // indigo
  aprovado_faturamento: '#059669', // emerald
  faturado: '#64748B',      // slate
};

const statusLabels = {
  criada: 'Criada',
  aguardando_inicio: 'Aguardando Início',
  em_andamento: 'Em Andamento',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
  aguardando_validacao: 'Aguardando Validação',
  aprovado_faturamento: 'Aprovado p/ Faturamento',
  faturado: 'Faturado',
};

// Component to fit bounds to all markers
const FitBounds = ({ missions }) => {
  const map = useMap();
  
  useEffect(() => {
    if (missions && missions.length > 0) {
      const validMissions = missions.filter(m => m.location_lat && m.location_lng);
      if (validMissions.length > 0) {
        const bounds = L.latLngBounds(
          validMissions.map(m => [m.location_lat, m.location_lng])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [missions, map]);
  
  return null;
};

const MissionsMap = ({ missions, onMissionClick, selectedStatus, className }) => {
  // Filter missions by status if provided
  const filteredMissions = selectedStatus && selectedStatus !== 'all'
    ? missions.filter(m => m.status === selectedStatus)
    : missions;

  // Filter out missions without valid coordinates
  const validMissions = filteredMissions.filter(
    m => m.location_lat && m.location_lng && 
    !isNaN(m.location_lat) && !isNaN(m.location_lng)
  );

  // Center on São Paulo by default
  const defaultCenter = [-23.5505, -46.6333];
  const defaultZoom = 8;

  return (
    <div className={`relative ${className}`} style={{ minHeight: '400px' }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%', borderRadius: '1rem' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {validMissions.length > 0 && <FitBounds missions={validMissions} />}
        
        {validMissions.map((mission) => (
          <Marker
            key={mission.id}
            position={[mission.location_lat, mission.location_lng]}
            icon={createIcon(statusColors[mission.status] || '#64748B')}
            eventHandlers={{
              click: () => onMissionClick && onMissionClick(mission),
            }}
          >
            <Popup>
              <div className="p-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: statusColors[mission.status] }}
                  />
                  <span className="text-xs font-medium text-slate-500">
                    {statusLabels[mission.status]}
                  </span>
                </div>
                <p className="font-mono font-bold text-slate-900 text-sm mb-1">
                  {mission.code}
                </p>
                <p className="text-slate-600 text-sm mb-2">
                  {mission.reason_description}
                </p>
                <p className="text-slate-500 text-xs">
                  {mission.location_address}
                </p>
                {onMissionClick && (
                  <button
                    onClick={() => onMissionClick(mission)}
                    className="mt-3 w-full py-2 px-3 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    Ver Detalhes
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 z-[1000]">
        <p className="text-xs font-medium text-slate-700 mb-2">Legenda</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {Object.entries(statusColors).slice(0, 6).map(([status, color]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-slate-600 truncate">
                {statusLabels[status]?.split(' ')[0]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Mission count */}
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg px-4 py-2 z-[1000]">
        <p className="text-sm font-medium text-slate-900">
          {validMissions.length} <span className="text-slate-500 font-normal">missões</span>
        </p>
      </div>
    </div>
  );
};

export default MissionsMap;
